import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Register } from './views/Register/Register';
import {
  QueryClient,
  QueryClientProvider,
  useIsFetching,
} from '@tanstack/react-query';
import { PageLoadingSpinner } from './components/Spinner';
import { DivingCylinders } from './views/DivingCylinders/DivingCylinders';
import { ClubCylinders } from './views/ClubCylinders/ClubCylinders';
import { UserSettings } from './components/UserSettings/UserSettings';
import { BlenderLogbook } from './views/BlenderLogbook';
import { Logbook } from './views/Logbook';
import { FillEvents } from './views/FillEvents';
import React, { type JSX } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GDPR } from './views/GDPR';
import { FrontPage } from './views/FrontPage/FrontPage';
import { PasswordResetRequest } from './views/PasswordResetRequest/PasswordResetRequest';
import { ResetPassword } from './views/ResetPassword/ResetPassword';
import { Login } from './views/Login/Login';
import { PrivateContent } from './components/common/PrivateContent';
import { Footer } from './components/Footer/Footer';
import { InvoicePage } from './views/Invoice/Invoice';
import { UsersPage } from './views/Users/Users';
import { ArchivedUsersPage } from './views/ArchivedUsers/ArchivedUsers';
import { GasPrices } from './views/GasPrices/GasPrices';
import { SiteNotices } from './views/SiteNotices/SiteNotices';

const QUERY_CLIENT = new QueryClient();

const BaseElement: React.FC = () => (
  <div className="position-absolute w-100 h-100 d-flex flex-column justify-content-between">
    <Outlet />
    <Footer />
  </div>
);




const Content: React.FC = () => {
  // @TODO: We need to do spinner thing better
  const isFetching = useIsFetching();

  return (
    <main>
      {isFetching ? <PageLoadingSpinner /> : null}
      <Routes>
        <Route element={<BaseElement />}>
          {/* Public routes */}
          <Route element={<FrontPage />}>
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route
              path={'request-password-reset'}
              element={<PasswordResetRequest />}
            />
            <Route path={'reset-password'} element={<ResetPassword />} />
          </Route>
          <Route path="gdpr" element={<GDPR />} />

          {/* Private routes */}
          <Route element={<PrivateContent />}>
            <Route path="diving-cylinder-set" element={<DivingCylinders />} />

            <Route path="fill-events" element={<FillEvents />} />
            <Route path="user" element={<UserSettings />} />
          </Route>

          {/* User only views */}
          <Route element={<PrivateContent userOnly />}>
            <Route path="logbook" element={<Logbook />} />
          </Route>

          {/* Blender only views */}
          <Route element={<PrivateContent blenderOnly userOnly />}>
            <Route path="blender-logbook" element={<BlenderLogbook />} />
          </Route>

          {/* Instructor only views */}
          <Route element={<PrivateContent instructorOnly />}>
            <Route path="club-cylinders" element={<ClubCylinders />} />
          </Route>

          {/* Admin only views */}
          <Route path="admin" element={<PrivateContent adminOnly />}>
            <Route path="invoice" element={<InvoicePage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="archived-users" element={<ArchivedUsersPage />} />
            <Route path="gas-prices" element={<GasPrices />} />
            <Route path="notices" element={<SiteNotices />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/logbook" />} />
        </Route>
      </Routes>
    </main>
  );
};

const App = (): JSX.Element => {
  return (
    <QueryClientProvider client={QUERY_CLIENT}>
      <ToastContainer className="toast-position" position={'top-right'} />
      <Content />
    </QueryClientProvider>
  );
};

export default App;
